import { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { fetchQuery, graphql } from 'react-relay';

import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getRelayEnvironment } from '@confluence/graphql-provider/entry-points/relayEnvironment.ts';

import type { ReactionAgentProfile } from '../utils/mapReactionActors.ts';

import type {
	useReactionAgentProfilesQuery,
	useReactionAgentProfilesQuery$data,
} from './__generated__/useReactionAgentProfilesQuery.graphql.ts';

export type AgentIdentifier = {
	agentId: string;
	agentType?: string;
};

const i18n = defineMessages({
	external: {
		id: 'reactions.external-agent.name',
		defaultMessage: 'External agent',
		description: 'Fallback name for an agent without a profile',
	},
	rovo: {
		id: 'reactions.rovo-agent.name',
		defaultMessage: 'Rovo',
		description: 'Rovo agent name in reactions',
	},
});

const AgentProfilesQuery = graphql`
	query useReactionAgentProfilesQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
		}
	}
`;

export const useReactionAgentProfiles = (
	_cloudId: string,
	agentIdentifiers: AgentIdentifier[],
): { agentsById: Map<string, ReactionAgentProfile>; fallbackAgentName: string } => {
	const { formatMessage } = useIntl();
	const fallbackAgentName = formatMessage(i18n.external);
	const rovoAgentName = formatMessage(i18n.rovo);
	const agentIds = useMemo(
		() =>
			Array.from(
				new Set(
					agentIdentifiers.flatMap(({ agentId, agentType }) =>
						agentType === 'convo-ai' && agentId && agentId !== agentType ? [agentId] : [],
					),
				),
			).sort(),
		[agentIdentifiers],
	);
	const accountIds = useMemo(
		() =>
			Array.from(
				new Set(
					agentIds.flatMap((agentId) => {
						const accountId = normalizeAccountIdFromIdentityAri(agentId);
						return accountId ? [accountId] : [];
					}),
				),
			),
		[agentIds],
	);
	const [users, setUsers] = useState<useReactionAgentProfilesQuery$data['users']>(null);
	const accountIdsKey = accountIds.join('\0');
	useEffect(() => {
		if (!accountIdsKey) {
			return;
		}
		const subscription = fetchQuery<useReactionAgentProfilesQuery>(
			getRelayEnvironment(),
			AgentProfilesQuery,
			{ accountIds: accountIdsKey.split('\0') },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: ({ users: fetchedUsers }) => setUsers(fetchedUsers),
			error: markErrorAsHandled,
		});
		return () => subscription.unsubscribe();
	}, [accountIdsKey]);
	const agentsById = useMemo(() => {
		const usersById = new Map((users ?? []).map((user) => [user.accountId, user]));
		const profiles = new Map<string, ReactionAgentProfile>(
			agentIds.map((agentId) => {
				const accountId = normalizeAccountIdFromIdentityAri(agentId);
				const user = accountId ? usersById.get(accountId) : undefined;
				return [
					agentId,
					user?.name?.trim()
						? {
								id: agentId,
								name: user.name.trim(),
								avatarUrl: user.picture?.trim() || undefined,
								identity_account_id: accountId,
							}
						: { id: agentId, name: fallbackAgentName, isExternalAgentFallback: true },
				];
			}),
		);
		if (
			agentIdentifiers.some(
				({ agentId, agentType }) => agentType === 'convo-ai' && agentId === agentType,
			)
		) {
			profiles.set('convo-ai', {
				id: 'convo-ai',
				name: rovoAgentName,
				agentIcon: 'rovo',
				isExternalAgentFallback: true,
			});
		}
		return profiles;
	}, [agentIds, agentIdentifiers, users, fallbackAgentName, rovoAgentName]);

	return { agentsById, fallbackAgentName };
};
